import { Html } from '@react-three/drei'
import { getThreeHexFromTheme } from '~/lib/utils'
import { useAppSelector } from '~/store/hooks'
import { Door } from '~/store/objects/types'
import { VectorVisualizer } from './DoorArrows'
import { SnapData } from '../util/snaplines'
import { modelToUI, uiToModel, vectorModelToUI } from '../util/units'
import { orthogonalVectorOfWallSegment, bisectLine } from '../util/walls'
import { useDistanceDimensions } from '~/hooks/useDistanceDimensions'
import { ARROW_OFFSET } from '../constants/zIndices'
import { Line3, Vector3 } from 'three'
import { inputStyles } from '~/ui/Field'
import { selectDenormalizedWalls } from '~/store/objects/selectors/walls'

const Z_FIGHTING_PREVENTION_EPSILON = 0.25

export function SnapPreview(props: { door: Pick<Door, 'width' | 'height'>; snapPoint: SnapData }) {
  const { objectID, snapPoint } = props.snapPoint
  const { x, y } = snapPoint
  const { width, height } = props.door

  const denormalizedSegments = useAppSelector(selectDenormalizedWalls).map(({ segments }) => segments)
  const wallSegment = denormalizedSegments.flat().find(({ id }) => id === objectID)!
  const { displayFormattedValue } = useDistanceDimensions()
  const thickness = wallSegment.thickness
  const is3D = useAppSelector(it => it.camera.is3D)

  const doorX = x
  const doorY = y
  const doorZ = modelToUI(height) / 2

  const doorVector = { x: doorX, y: doorY, z: doorZ }

  const doorWidth = modelToUI(width)
  const doorHeight = modelToUI(height)
  const doorThickness = modelToUI(thickness) + Z_FIGHTING_PREVENTION_EPSILON

  const color = getThreeHexFromTheme('three.valid')

  const rotation = orthogonalVectorOfWallSegment(wallSegment)

  const { insetEdge } = wallSegment
  const start = vectorModelToUI({ x: insetEdge.start.x, y: insetEdge.start.y, z: 0 })
  const end = vectorModelToUI({ x: insetEdge.end.x, y: insetEdge.end.y, z: 0 })
  const line = new Line3(start, end)
  const projectedDoorVector = line.closestPointToPoint(new Vector3().copy(doorVector), true, new Vector3())

  const { v1, v2, v1Half, v2Half } = bisectLine(start, end, projectedDoorVector)

  const d1 = displayFormattedValue(uiToModel(v1.length()))
  const d2 = displayFormattedValue(uiToModel(v2.length()))
  const className = inputStyles({ isOnCanvas: true, class: 'pointer-events-none' })

  return (
    <>
      <mesh position={[doorX, doorY, doorZ]} rotation={rotation}>
        <boxGeometry args={[doorThickness, doorWidth, doorHeight]} />
        <meshLambertMaterial color={color} />
      </mesh>
      {!is3D && (
        <mesh position={[doorX, doorY, modelToUI(wallSegment.height)]} rotation={rotation}>
          <boxGeometry args={[doorThickness, doorWidth, 1]} />
          <meshLambertMaterial color={color} />
        </mesh>
      )}
      <group position={[doorX, doorY, modelToUI(wallSegment.height) + ARROW_OFFSET]}>
        <VectorVisualizer vector={v1} color="black" />
        <Html position={v1Half} center className={className}>
          {d1}
        </Html>
        <VectorVisualizer vector={v2} color="black" />
        <Html position={v2Half} center className={className}>
          {d2}
        </Html>
      </group>
    </>
  )
}
